import { useMemo } from 'react';
import { IVolData } from '../../../../../providers/widgets/footprint-volatility-curves-provider/footprint-volatility-curves-provider';

import ReactDOMServer from 'react-dom/server';

const colorsArr = ['#1FADAD', '#DD7A2B', '#FFC900', '#8F7DE8', '#80CAFF'];

const VOL_KEYS = [
  '5P',
  '10P',
  '15P',
  '20P',
  '25P',
  '30P',
  '35P',
  '40P',
  'DN',
  '40C',
  '35C',
  '30C',
  '25C',
  '20C',
  '15C',
  '10C',
  '5C',
];

const interpolateColor = (color1: string, color2: string, factor: number) => {
  const hex = (color: string) => {
    const hex = color.replace('#', '');
    if (hex.length === 3) {
      const expandedHex = hex
        .split('')
        .map((char) => char + char)
        .join('');

      return expandedHex;
    }
    return hex;
  };

  const hex1 = hex(color1);
  const hex2 = hex(color2);

  const r1 = parseInt(hex1.substring(0, 2), 16);
  const g1 = parseInt(hex1.substring(2, 4), 16);
  const b1 = parseInt(hex1.substring(4, 6), 16);

  const r2 = parseInt(hex2.substring(0, 2), 16);
  const g2 = parseInt(hex2.substring(2, 4), 16);
  const b2 = parseInt(hex2.substring(4, 6), 16);

  const r = Math.round(r1 + factor * (r2 - r1));
  const g = Math.round(g1 + factor * (g2 - g1));
  const b = Math.round(b1 + factor * (b2 - b1));

  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
};

const getColor = (hoursPast: number, maxHours: number = 24) => {
  if (hoursPast === 0) {
    return colorsArr[0];
  }

  const factor = maxHours > 1 ? (hoursPast - 1) / (maxHours - 1) : 0;
  const index = Math.floor(factor * (colorsArr.length - 1));
  const nextIndex = Math.min(index + 1, colorsArr.length - 1);
  const localFactor = (factor * (colorsArr.length - 1)) % 1;
  return interpolateColor(colorsArr[index], colorsArr[nextIndex], localFactor);
};

export const useProcessFootprintVolatilityData = (rawData: IVolData[]) => {
  return useMemo(() => {
    const xAxis = {
      type: 'category',
      data: VOL_KEYS,
      boundaryGap: true,
      axisTick: {
        alignWithLabel: true,
      },
      axisLabel: {
        align: 'center',
      },
    };

    const yAxis = {
      type: 'value',
      position: 'right',
      axisPointer: { label: { margin: 10 } },
      axisLabel: {
        align: 'left',
        formatter: (value: any) => (value * 100).toFixed(2) + '%',
      },
    };

    const tooltip = {
      trigger: 'item' as const,
      formatter: (params: any) => {
        const { name, value, seriesIndex, dataIndex, color, seriesId, seriesName } = params;

        const proximalValues = series.filter((item: any, index: number) => {
          if (index === seriesIndex || item.name === 'expires') return false;

          const d = item.data[dataIndex];

          return Math.abs(d - value) <= 0.01;
        });

        const renderItem = (color: string, name: string, value: number) => {
          return (
            <div className="tooltip-item" key={name}>
              <div className="mark" style={{ backgroundColor: color }} />
              <div className="label">{name}</div>
              <div className="value">{(value * 100).toFixed(2) + '%'}</div>
            </div>
          );
        };

        const hoursAgo = seriesId?.split('_')?.[0] || 0;
        const displayName = seriesName === 'expires' ? '0 hours ago' : `${hoursAgo} hours ago`;

        const element = (
          <div className="tooltip">
            <span className="title">{name}</span>
            <div className="tooltip-items">
              {renderItem(color, displayName, value)}
              {proximalValues.map((item: any) => {
                const color = item.color;
                const itemHoursAgo = item.id?.split('_')?.[0] || 0;
                const name = `${itemHoursAgo} hours ago`;
                const value = item.data[dataIndex];

                return renderItem(color, name, value);
              })}
            </div>
          </div>
        );

        return ReactDOMServer.renderToStaticMarkup(element);
      },
    };

    if (!Array.isArray(rawData) || rawData.length === 0) {
      return { series: [], xAxis, yAxis, tooltip };
    }

    const currentData = rawData.find((d) => d.date === 'now');

    if (!currentData) {
      return { series: [], xAxis, yAxis, tooltip };
    }

    const series = rawData.map((item) => {
      if (item.date === 'now') {
        return {
          data: currentData?.data,
          type: 'line',
          smooth: true,
          color: '#26D9D9',
          triggerLineEvent: true,
          name: 'expires',
          showAllSymbol: true,
          symbol: 'circle',
          symbolSize: 10,
        };
      } else {
        return {
          data: item.data,
          type: 'scatter',
          smooth: true,
          animation: false,
          color: getColor(item.hoursPast),
          name: item.date,
          id: item.hoursPast + '_' + item.date,
          showAllSymbol: true,
          symbol: 'circle',
          symbolSize: 4,
        };
      }
    });

    // const visualMap = {
    //   min: minHoursPast,
    //   max: maxHoursPast,
    //   dimension: 2,
    //   orient: 'horizontal',
    //   left: 'center',
    //   bottom: 10,
    //   inRange: {
    //     color: ['#00E5E5', '#FFD700', '#FFA500', '#8A2BE2'],
    //   },
    //   text: [`${maxHoursPast} hours ago`, `${minHoursPast}h`],
    //   calculate: false,
    // };

    return {
      series,
      xAxis,
      yAxis,
      visualMap: {},
      tooltip,
    };
  }, [rawData]);
};
